import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTicketsStore } from '../../../store/ticketsStore';
import { TicketCategory, TicketPriority } from '../../../services/ticketsApi';
import { listMyCustomerOrders, Order } from '../../../services/ordersApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './CustomerReportIssue.module.css';

/**
 * VP-CUST-004: customer-facing "Report Issue" form.
 *
 * QuickActions previously sent Customers to `/support`, which is
 * Call-Center-only in App.tsx — an Access Denied dead end. The ticket-
 * creation endpoint (POST /tickets) already fully supports the Customer
 * role: it auto-scopes customer_id and source=customer_portal server-side
 * (routes/tickets.ts), and accepts an optional order_id link. This is a
 * standalone customer-scoped form (not the Call-Center CreateTicketModal,
 * which exposes internal-only fields like assignment) built directly on
 * top of the same ticketsStore.create() used elsewhere in the app.
 */

const CUSTOMER_CATEGORIES: { value: TicketCategory; label: string }[] = [
  { value: 'customer', label: 'General / Account Issue' },
  { value: 'delivery', label: 'Delivery Issue' },
  { value: 'payment', label: 'Payment / Invoice Issue' },
  { value: 'returns', label: 'Return / Dispute' },
  { value: 'other', label: 'Other' },
];

const CustomerReportIssue: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const create = useTicketsStore((s) => s.create);
  const saving = useTicketsStore((s) => s.saving);

  const presetCategory = searchParams.get('category') as TicketCategory | null;
  const [category, setCategory] = useState<TicketCategory>(
    presetCategory && CUSTOMER_CATEGORIES.some((c) => c.value === presetCategory) ? presetCategory : 'customer'
  );
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [orderId, setOrderId] = useState<string>('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    listMyCustomerOrders({ limit: 50 })
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!subject.trim()) {
      setError('Please enter a subject.');
      return;
    }
    try {
      const created = await create({
        category,
        priority,
        subject: subject.trim(),
        description: description.trim() || undefined,
        order_id: orderId ? Number(orderId) : undefined,
      });
      setSuccess(`Ticket ${created.ticket_number} created — our team will follow up.`);
      setSubject('');
      setDescription('');
      setOrderId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create ticket');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h2 className={styles.title}>Report an Issue</h2>
        <p className={styles.subtitle}>Tell us what happened and our support team will get back to you.</p>

        <form className={styles.form} onSubmit={handleSubmit}>
          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.success}>{success}</div>}

          <div className={styles.field}>
            <label className={styles.label} htmlFor="issue-category">Issue Type</label>
            <select
              id="issue-category"
              className={styles.select}
              value={category}
              onChange={(e) => setCategory(e.target.value as TicketCategory)}
            >
              {CUSTOMER_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="issue-order">Related Order (optional)</label>
            <select
              id="issue-order"
              className={styles.select}
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
            >
              <option value="">None</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>{formatOrderId(o.id)}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="issue-priority">Priority</label>
            <select
              id="issue-priority"
              className={styles.select}
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="issue-subject">Subject *</label>
            <input
              id="issue-subject"
              className={styles.input}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Short summary of the issue"
              maxLength={200}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="issue-description">Description</label>
            <textarea
              id="issue-description"
              className={styles.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add any details that will help us resolve this quickly"
            />
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={() => navigate(-1)}>
              Cancel
            </button>
            <button type="submit" className={styles.submitBtn} disabled={saving}>
              {saving ? 'Submitting…' : 'Submit Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const CustomerReportIssuePage: React.FC = () => <CustomerReportIssue />;

export { CustomerReportIssue };
export default CustomerReportIssue;
